import { CalendarDays, SlidersHorizontal, X } from 'lucide-react';
import { useState } from 'react';
import { COMPARISONS } from '@crm/shared/contracts';
import { parseBusinessDate, type BusinessDate } from '@crm/shared/time';
import { FreshnessIndicator } from '../../components/FreshnessIndicator.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover.tsx';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet.tsx';
import { useToday } from '../../lib/clock.tsx';
import { formatDate } from '../../lib/format.ts';
import { useT } from '../../lib/i18n.tsx';
import {
  checkPeriod,
  detectPreset,
  PRESETS,
  presetPeriod,
  type Preset,
} from '../../lib/periods.ts';
import { useFilters, useMe, type OverviewResponse } from '../../lib/queries.ts';
import { useDashboardParams, type Comparison } from '../../lib/url-state.ts';

type Meta = OverviewResponse['meta'];

function tryDate(value: string): BusinessDate | null {
  try {
    return parseBusinessDate(value);
  } catch {
    return null;
  }
}

function PeriodPicker() {
  const t = useT();
  const today = useToday();
  const [params, update] = useDashboardParams();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState<string>(params.from);
  const [to, setTo] = useState<string>(params.to);
  const preset = detectPreset({ from: params.from, to: params.to }, today);

  const customFrom = tryDate(from);
  const customTo = tryDate(to);
  const problem =
    customFrom && customTo ? checkPeriod({ from: customFrom, to: customTo }) : 'invalid';

  const choose = (value: Preset): void => {
    const period = presetPeriod(value, today);
    if (!period) return;
    update({ from: period.from, to: period.to });
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setFrom(params.from);
          setTo(params.to);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" aria-label={t.period.label}>
          <CalendarDays aria-hidden="true" className="size-4" />
          <span>{t.period.presets[preset]}</span>
          <span className="text-muted-foreground">
            {t.period.range(formatDate(params.from), formatDate(params.to))}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3">
        <ul className="grid gap-1">
          {PRESETS.filter((value) => value !== 'custom').map((value) => (
            <li key={value}>
              <button
                type="button"
                aria-pressed={preset === value}
                onClick={() => choose(value)}
                className="w-full rounded-md px-2 py-2.5 text-left text-sm hover:bg-muted lg:py-1.5 aria-pressed:bg-accent aria-pressed:font-medium aria-pressed:text-primary"
              >
                {t.period.presets[value]}
              </button>
            </li>
          ))}
        </ul>
        <form
          className="space-y-2 border-t pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (customFrom && customTo && !problem) {
              update({ from: customFrom, to: customTo });
              setOpen(false);
            }
          }}
        >
          <p className="text-sm font-medium">{t.period.presets.custom}</p>
          <label className="block text-xs">
            {t.period.from}
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-card px-2 text-sm"
            />
          </label>
          <label className="block text-xs">
            {t.period.to}
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-card px-2 text-sm"
            />
          </label>
          {problem && customFrom && customTo ? (
            <p role="alert" className="text-xs text-destructive">
              {problem === 'tooLong' ? t.period.tooLong : t.period.invalid}
            </p>
          ) : null}
          <Button type="submit" size="sm" disabled={problem !== null}>
            {t.common.apply}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

function ComparePicker() {
  const t = useT();
  const [params, update] = useDashboardParams();
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{t.compare.label}</span>
      <select
        value={params.compare}
        onChange={(event) => update({ compare: event.target.value as Comparison })}
        className="h-11 rounded-md border border-input bg-card px-2 lg:h-10"
      >
        {COMPARISONS.map((value) => (
          <option key={value} value={value}>
            {t.compare[value]}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Salesperson and source filters: a bottom sheet, which also suits phones. */
function FilterSheet() {
  const t = useT();
  const filters = useFilters();
  const [params, update] = useDashboardParams();
  const salespeople = filters.data?.data.salespeople ?? [];
  const sources = filters.data?.data.sources ?? [];
  const count = params.salespersonId.length + params.sourceId.length;

  const toggle = (name: 'salespersonId' | 'sourceId', id: string): void => {
    const current = params[name];
    update({ [name]: current.includes(id) ? current.filter((x) => x !== id) : [...current, id] });
  };

  const group = (
    name: 'salespersonId' | 'sourceId',
    title: string,
    items: readonly { id: string; label: string }[],
  ) => (
    <fieldset className="space-y-1">
      <legend className="text-sm font-medium">{title}</legend>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.filters.none}</p>
      ) : (
        items.map((item) => (
          <label key={item.id} className="flex min-h-11 items-center gap-3 text-sm lg:min-h-9">
            <input
              type="checkbox"
              checked={params[name].includes(item.id)}
              onChange={() => toggle(name, item.id)}
              className="size-5 lg:size-4"
            />
            {item.label}
          </label>
        ))
      )}
    </fieldset>
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" aria-label={t.filters.open}>
          <SlidersHorizontal aria-hidden="true" className="size-4" />
          {t.filters.title}
          {count > 0 ? <Badge variant="accent">{count}</Badge> : null}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="lg:inset-x-auto lg:right-0 lg:top-0 lg:max-h-none lg:w-80 lg:rounded-none lg:border-l lg:border-t-0"
      >
        <SheetTitle className="text-base font-semibold">{t.filters.title}</SheetTitle>
        <SheetDescription className="sr-only">{t.filters.title}</SheetDescription>
        {group(
          'salespersonId',
          t.filters.salesperson,
          salespeople.map((s) => ({
            id: s.id,
            label: s.isActive ? s.fullName : `${s.fullName} (${t.common.inactive})`,
          })),
        )}
        {group(
          'sourceId',
          t.filters.source,
          sources.map((s) => ({ id: s.id, label: s.name })),
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => update({ salespersonId: [], sourceId: [] })}
            disabled={count === 0}
          >
            {t.filters.clearAll}
          </Button>
          <SheetClose asChild>
            <Button size="sm">{t.common.close}</Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ActiveFilters() {
  const t = useT();
  const filters = useFilters();
  const [params, update] = useDashboardParams();
  const names = new Map<string, string>([
    ...(filters.data?.data.salespeople ?? []).map((s) => [s.id, s.fullName] as const),
    ...(filters.data?.data.sources ?? []).map((s) => [s.id, s.name] as const),
  ]);
  const chips = [
    ...params.salespersonId.map((id) => ({ name: 'salespersonId' as const, id })),
    ...params.sourceId.map((id) => ({ name: 'sourceId' as const, id })),
  ];
  if (chips.length === 0) return null;
  return (
    <ul aria-label={t.filters.active} className="flex flex-wrap gap-2">
      {chips.map((chip) => {
        const label = names.get(chip.id) ?? t.common.unknown;
        return (
          <li key={`${chip.name}-${chip.id}`}>
            <button
              type="button"
              aria-label={t.filters.remove(label)}
              onClick={() =>
                update({ [chip.name]: params[chip.name].filter((id) => id !== chip.id) })
              }
              className="inline-flex min-h-10 items-center gap-1.5 rounded-full border bg-accent px-3 py-1.5 text-sm font-medium text-primary lg:min-h-0 lg:px-2.5 lg:py-1 lg:text-xs"
            >
              {label}
              <X aria-hidden="true" className="size-4 lg:size-3" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The global controls of every period page: period, comparison, filters and data freshness.
 * Everything lives in the URL, so a view can be shared and the back button works.
 */
export function Controls({
  meta,
  compare = true,
  filters = true,
}: {
  meta: Meta | undefined;
  compare?: boolean;
  filters?: boolean;
}) {
  const t = useT();
  const me = useMe();
  // A salesperson is scoped by the server to their own data: a salesperson filter would mislead.
  const scoped = me.data?.role === 'salesperson';
  return (
    <section aria-label={t.filters.title} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <PeriodPicker />
        {compare ? <ComparePicker /> : null}
        {filters && !scoped ? <FilterSheet /> : null}
        <div className="ml-auto">
          <FreshnessIndicator freshness={meta?.dataFreshness} />
        </div>
      </div>
      {compare && meta?.comparisonPeriod ? (
        <p className="text-xs text-muted-foreground">
          {t.compare.with(
            t.period.range(
              formatDate(meta.comparisonPeriod.from),
              formatDate(meta.comparisonPeriod.to),
            ),
          )}
        </p>
      ) : null}
      {scoped ? <p className="text-xs text-muted-foreground">{t.filters.scopeNote}</p> : null}
      {filters ? <ActiveFilters /> : null}
    </section>
  );
}
